import RefreshIcon from '@mui/icons-material/Refresh'
import {
  ButtonBase,
  Box,
  Chip,
  Fab,
  Grid,
  Paper,
  Popover
} from '@mui/material'
import { styled as muiStyled } from '@mui/system'
import dayjs from 'dayjs'
import { components } from 'react-select'
import { AsyncPaginate } from 'react-select-async-paginate'
import {
  currency_list
} from 'src/consumer/constants'
import styled from 'styled-components'
import { getCurrencySymbol } from './MentorEventsHelpers'
import { VISIT_EVENT_TYPES } from 'src/consumer/constants'

export const EventsPageMainItemsContainer = styled.div`
  padding: 0;
  gap: 0;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  width: 100%;
  
  @media (min-width: 768px) {
    padding: 0;
  }
`

export const EventsPageItemContainer = styled.div`
  width: 100%;
  padding: 30px 15px;
  border: 1px solid #cccccc;
  ${(props) => props?.enabled && 'border-top: 2px solid #8247f5'};
  ${(props) => !props?.enabled && 'background: #cecece'};
  border-radius: 4px;
  box-shadow: 0 1px 6px 0 rgb(0 0 0/10%);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
  @media (min-width: 768px) {
    width: 280px;
  }
`

export const NewEventTypeButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  width: calc(100% - 40px);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  border: ${(props) => (props.noBorder ? 'unset' : '1px solid rgba(0, 0, 0, 0.12)')};
  padding: 6px 14px;
  background: ${(props) => 
    props?.selected 
      ? (props?.selectedBgColor || 'linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(67, 56, 202, 0.9) 100%)')
      : 'rgba(255, 255, 255, 0.8)'};
  color: ${(props) => (props?.selected ? '#fff' : 'rgba(0, 0, 0, 0.87)')};
  cursor: ${(props) => (props.onClick || props.style?.cursor === 'pointer' ? 'pointer' : 'default')};
  transition: all 0.2s ease;
  box-shadow: ${(props) => 
    props?.selected 
      ? '0 2px 8px rgba(99, 102, 241, 0.25), 0 1px 3px rgba(0, 0, 0, 0.1)'
      : '0 1px 2px rgba(0, 0, 0, 0.05)'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  
  &:hover {
    ${(props) => 
      !props?.selected && !props.style?.cursor && props.style?.cursor !== 'default'
        ? `
          background: rgba(99, 102, 241, 0.08);
          border-color: rgba(99, 102, 241, 0.3);
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
        `
        : ''}
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (min-width: 768px) {
    padding: 7px 16px;
    font-size: 13px;
    width: auto;
    border-radius: 22px;
  }
`

export const BookedEventContainer = styled.div`
  ${(props) => {
    if (String(props.status || '').toLowerCase() === 'cancelled') {
      return `
        --booking-card-bg: linear-gradient(135deg, rgba(254, 242, 242, 0.98) 0%, rgba(255, 247, 247, 0.96) 52%, rgba(254, 226, 226, 0.84) 100%);
        --booking-card-border: rgba(220, 38, 38, 0.46);
        --booking-card-shadow: rgba(185, 28, 28, 0.2);
      `
    }

    return `
      --booking-card-bg: linear-gradient(135deg, #ffffff 0%, #ffffff 62%, ${props.$statusSurfaceColor || 'rgba(248, 250, 252, 0.92)'} 100%);
      --booking-card-border: ${props.$statusBorderColor || 'rgba(203, 213, 225, 0.95)'};
      --booking-card-shadow: ${props.$statusShadowColor || 'rgba(15, 23, 42, 0.08)'};
    `
  }}
  border: ${(props) => {
    if (props.visitEventType === VISIT_EVENT_TYPES.emergency) {
      return '2px solid rgba(239, 68, 68, 0.36)'
    }

    return '1px solid var(--booking-card-border)'
  }};
  border-radius: 8px;
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  display: block;
  background: var(--booking-card-bg);
  box-shadow:
    0 22px 46px rgba(15, 23, 42, 0.15),
    0 4px 14px var(--booking-card-shadow);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  position: relative;
  overflow: hidden;
  ${(props) =>
    String(props.status || '').toLowerCase() === 'cancelled' &&
    `
      box-shadow:
        0 22px 46px rgba(185, 28, 28, 0.16),
        0 0 0 1px rgba(220, 38, 38, 0.08),
        0 4px 14px var(--booking-card-shadow);
    `}
  ${(props) =>
    props.$attention &&
    `
      animation: booking-card-attention-pulse 1.5s ease-in-out infinite;
      border-color: ${props.$attentionTone === 'upcoming' ? 'rgba(37, 99, 235, 0.34)' : 'rgba(245, 158, 11, 0.36)'};
    `}
  
  &:hover {
    box-shadow:
      0 26px 54px rgba(15, 23, 42, 0.18),
      0 8px 20px var(--booking-card-shadow);
    transform: translateY(-1px);
  }

  @keyframes booking-card-attention-pulse {
    0%,
    100% {
      box-shadow: 0 22px 46px rgba(15, 23, 42, 0.15), 0 0 0 0 rgba(245, 158, 11, 0.14);
    }
    50% {
      box-shadow: 0 26px 54px rgba(15, 23, 42, 0.18), 0 0 0 4px rgba(245, 158, 11, 0.08);
    }
  }
  
  @media (min-width: 768px) {
    padding: 14px;
  }
  
  @media (max-width: 767px) {
    padding: 10px;
    margin-bottom: 12px;
  }
`

export const BookedEventTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 4px;
  line-height: 1.5;
  
  @media (max-width: 767px) {
    font-size: 14px;
  }
`

export const InfoSection = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
`

export const InfoRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #4b5563;
  font-size: 13px;
  line-height: 1.4;
`

export const InfoLabel = styled.span`
  font-weight: 500;
  color: #6b7280;
  min-width: 70px;
  font-size: 12px;
`

export const InfoValue = styled.span`
  font-weight: 400;
  color: #1f2937;
  flex: 1;
  font-size: 13px;
`

export const JoiningLink = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #6366f1;
  cursor: pointer;
  text-decoration: none;
  padding: 6px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: inline-block;
  
  &:hover {
    background-color: rgba(99, 102, 241, 0.1);
    color: #4f46e5;
  }
  
  @media (max-width: 767px) {
    font-size: 12px;
    padding: 5px 10px;
  }
`

export const StyledPanelMemberReactSelect = styled(AsyncPaginate)`
  width: 300px;
  @media (min-width: 768px) {
    width: 400px;
  }
`

export const StyledEventsRefreshIcon = styled(RefreshIcon)`
  cursor: pointer;
  margin-right: -3px;
  &:hover {
    transform: rotate(135deg);
  }
  transition: all 0.3s ease !important;
`

export const StyledLocationFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 5px 10px;
  border: 1px solid #00000033;
  width: 100%;
  border-radius: 7px;
`

export const StyledClinicLocationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
`
export const StyledRestLocationFieldsContainer = styled.div`
  display: grid;
  width: 100%;
  align-items: flex-start;
  gap: 5px;
  grid-template-columns: repeat(2, 1fr);
`

export const MentorEventsPageStyledDialogPaper = muiStyled(Paper)(
  ({ theme }) => `
  width: 100%;
  max-width: 900px !important;
  margin: 0px !important;
  max-height: 80vh !important;
  height: 100%;
  @media (min-width: 576px) {
    margin: 10px !important;
  }
`,
)

export const AddPricingFab = muiStyled(Fab)(
  ({ theme }) => `
  background: #ac78ff;
  color: white;
  &:hover {
    background: #8247f5;
  }
`,
)

export const CancelAddCustomPrice = muiStyled(ButtonBase)(
  ({ theme }) => `
  padding: 8px 16px;
  border-radius: 7px;
  font-weight: 500;
`,
)
export const AddCustomPriceButton = muiStyled(ButtonBase)(
  ({ theme }) => `
  padding: 8px 16px;
  border-radius: 7px;
  color: white;
  background: #ac78ff;
  font-weight: 500;
  transition: all 150ms ease-in-out;
  &:disabled {
    background: grey;
  }
  
`,
)
export const ChangesWillNotBeSavedButton = muiStyled(ButtonBase)(
  ({ theme }) => `
  padding: 8px 16px;
  border-radius: 7px;
  color: white;
  background: #d5872f;
  font-weight: 500;
  
`,
)

export const AddCountryPricingPopover = muiStyled(Popover)(
  ({ theme }) => `
  .MuiPopover-paper {
    min-width: 300px;
  }`,
)

export const AddCustomPriceSubHeading = muiStyled('div')(
  ({ theme }) => `
  font-size: 16px;
  font-weight: 400;
`,
)

export const CancelConfirmationButton = muiStyled(ButtonBase)(
  ({ theme }) => `
  padding: 8px 16px;
  border-radius: 7px;
  font-weight: 500;
`,
)
export const DeletePriceButton = muiStyled(ButtonBase)(
  ({ theme }) => `
  padding: 8px 16px;
  border-radius: 7px;
  font-weight: 500;
  background: #d44040;
  color: white;
`,
)
export const SavePlanButton = muiStyled(ButtonBase)(
  ({ theme }) => `
  padding: 8px 16px;
  border-radius: 7px;
  font-weight: 500;
  background: #29a429;
  color: white;
  &:disabled {
    background: grey;
  }
`,
)

export const CountryPricingContainer = muiStyled(Grid)(
  ({ theme }) => `
`,
)

export const ChangeViewPricingHTMLSelect = muiStyled('select')`
  outline: none;
  padding: 6px;
  text-align:center;
background: ${({ invalid }) => (invalid ? '#edbbbb' : '#ebebeb')};
border-radius: 7px;
width: 100%;
  -webkit-appearance: none;
  -moz-appearance: none;
  text-indent: 1px;
`
export const ChangeViewPricingHTMLSelectOption = muiStyled('option')`
text-align:left;
`

// components, functions and helpers:

const formatDuration = (minutes) => {
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  if (hours === 0) {
    return `${remainingMinutes} mins`
  } else if (remainingMinutes === 0) {
    return `${hours} hr`
  } else {
    return `${hours} hr ${remainingMinutes} mins`
  }
}

export const getDurationOptions = () => {
  const options = []
  const durations = [5, 10, 15, 20, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180]

  for (const duration of durations) {
    const timeValue = `${duration}`
    const displayText = formatDuration(duration)

    options.push(
      <option key={timeValue} value={timeValue}>
        {displayText}
      </option>,
    )
  }
  return options
}

export const getCurrencyOptions = () => {
  return currency_list?.map((item, index) => {
    return (
      <option key={index} value={item?.code}>
        {item?.name} ({item?.symbol})
      </option>
    )
  })
}

export function SubscriptionOption(props) {
  const currencySymbolToUse = getCurrencySymbol(props)
  const viewPriceCountry = props?.selectProps?.viewPriceCountry

  const pricingMatrix = props.data?.pricingMatrix
    ? Object.keys(props.data.pricingMatrix).reduce((acc, key) => {
        acc[key.toLowerCase()] = props.data.pricingMatrix[key]
        return acc
      }, {})
    : {}

  const selectedPriceToShow = pricingMatrix?.[viewPriceCountry]?.price || props.data?.price
  const finalPriceToShow =
    pricingMatrix?.[viewPriceCountry]?.discountedPrice || props.data?.finalPrice

  const showStrikeThrough = selectedPriceToShow !== finalPriceToShow

  return (
    <components.Option {...props}>
      {props?.data?.id === 'create-plan' ? (
        'Set Custom Price'
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {showStrikeThrough ? (
            <div>
              <span>{currencySymbolToUse}</span>
              <span style={{ textDecoration: 'line-through' }}>{selectedPriceToShow}</span> @{' '}
              <span style={{ fontWeight: '700' }}>{currencySymbolToUse}</span>
              <span style={{ fontWeight: '700' }}>{finalPriceToShow}</span>
            </div>
          ) : (
            <div>
              <span>{currencySymbolToUse}</span>
              <span style={{ fontWeight: '700' }}>{finalPriceToShow || 0}</span>
            </div>
          )}
          {!!props.data?.mentorId && <Chip color="default" label="Editable" size="small" />}
        </div>
      )}
    </components.Option>
  )
}

export function SubscriptionValue(props) {
  const currencySymbolToUse = getCurrencySymbol(props)
  const viewPriceCountry = props?.selectProps?.viewPriceCountry

  const selectedPriceToShow =
    props.data?.pricingMatrix?.[viewPriceCountry]?.price || props.data?.price
  const finalPriceToShow =
    props.data?.pricingMatrix?.[viewPriceCountry]?.discountedPrice || props.data?.finalPrice

  const showStrikeThrough = selectedPriceToShow !== finalPriceToShow
  // useEffect(() => {
  //   console.log({ viewPriceCountry, pricingMatrix: props.data?.pricingMatrix })
  // }, [viewPriceCountry])

  return (
    <components.SingleValue {...props}>
      {showStrikeThrough ? (
        <div>
          <span>{currencySymbolToUse}</span>
          <span style={{ textDecoration: 'line-through' }}>{selectedPriceToShow}</span> @{' '}
          <span style={{ fontWeight: '700' }}>{currencySymbolToUse}</span>
          <span style={{ fontWeight: '700' }}>{finalPriceToShow}</span>
        </div>
      ) : (
        <div>
          <span>{currencySymbolToUse}</span>
          <span style={{ fontWeight: '700' }}>{finalPriceToShow || 0}</span>
        </div>
      )}
    </components.SingleValue>
  )
}
